import axios from 'axios';

const isLocalHost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
const configuredApiUrl = isLocalHost
  ? process.env.REACT_APP_API_URL_DEV || process.env.REACT_APP_API_URL || 'http://127.0.0.1:8081/api'
  : process.env.REACT_APP_API_URL_PROD || process.env.REACT_APP_API_URL || 'https://photomatch.ravierkonan.com/api';
// Laravel binds IPv4 locally. Expo can occupy ::1 on the same port, so a
// localhost request may reach its dev server instead of the Laravel API.
const localApiUrl = new URL(configuredApiUrl);
if (isLocalHost && localApiUrl.hostname === 'localhost') {
  localApiUrl.hostname = '127.0.0.1';
}
const API_URL = isLocalHost ? localApiUrl.toString() : configuredApiUrl;
const FLASK_API_URL = process.env.REACT_APP_FLASK_API_URL || 'http://localhost:5000/api';
const EVENT_PHOTO_UPLOAD_TIMEOUT_MS = 600000;
const FACE_SCAN_TIMEOUT_MS = 900000;

let refreshPromise = null;

const refreshAuthToken = () => {
  if (!refreshPromise) {
    refreshPromise = axios.post(`${API_URL}/auth/refresh`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      timeout: 120000,
    }).then(response => {
      if (!response.data.success || !response.data.token) {
        throw new Error('Token refresh returned no token');
      }
      localStorage.setItem('authToken', response.data.token);
      return response.data.token;
    }).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

const tokenExpiresSoon = (token) => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '=')));
    return typeof exp === 'number' && exp * 1000 <= Date.now() + 60000;
  } catch {
    return false;
  }
};

const clearStoredAuth = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('userData');
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 120000,
});

const flaskApi = axios.create({
  baseURL: FLASK_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 120000,
});

// Payment request logging.
//
// Payment bodies carry customer details, so this stays off in production
// builds unless REACT_APP_DEBUG_PAYMENTS=true is set explicitly.
const PAYMENT_ENDPOINTS = /^\/(purchases\/checkout-cart|payments\/(cinetpay|pispi)\/|subscription\/checkout\/)/;

const PAYMENT_DEBUG = process.env.NODE_ENV !== 'production'
  || process.env.REACT_APP_DEBUG_PAYMENTS === 'true';

const isPaymentRequest = (url) => typeof url === 'string' && PAYMENT_ENDPOINTS.test(url);

const describeEndpoint = (config = {}) => ({
  method: (config.method || 'get').toUpperCase(),
  url: `${config.baseURL || ''}${config.url || ''}`,
});

const maskedHeaders = (headers) => {
  const plain = headers && typeof headers.toJSON === 'function'
    ? headers.toJSON()
    : { ...headers };
  const raw = plain.Authorization || plain.authorization;

  if (typeof raw === 'string') {
    const token = raw.replace(/^Bearer\s+/i, '');
    delete plain.authorization;
    plain.Authorization = token.length > 12
      ? `Bearer ${token.slice(0, 8)}…${token.slice(-4)} (${token.length} chars, masked)`
      : 'Bearer ***';
  }

  return plain;
};

const logPaymentRequest = (config) => {
  const { method, url } = describeEndpoint(config);

  console.groupCollapsed(`%c[payment] → ${method} ${url}`, 'color:#ff7a00;font-weight:bold');
  console.log('endpoint:', url);
  console.log('headers:', maskedHeaders(config.headers));
  console.log('body:', config.data);
  console.groupEnd();
};

const logPaymentResponse = (response) => {
  const { method, url } = describeEndpoint(response.config);

  console.groupCollapsed(`%c[payment] ← ${response.status} ${method} ${url}`, 'color:#2e7d32;font-weight:bold');
  console.log('endpoint:', url);
  console.log('response:', response.data);
  console.groupEnd();
};

const logPaymentError = (error) => {
  const config = error.config || {};
  const { method, url } = describeEndpoint(config);
  const status = error.response?.status || 'network error';

  console.groupCollapsed(`%c[payment] ✕ ${status} ${method} ${url}`, 'color:#c0392b;font-weight:bold');
  console.log('endpoint:', url);
  console.log('headers:', maskedHeaders(config.headers));
  console.log('body:', config.data);
  console.log('error:', error.response?.data || error.message);
  console.groupEnd();
};



// Request Interceptor
// Request Interceptor
api.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('authToken');
    if (token && !config.url?.startsWith('/auth/') && tokenExpiresSoon(token)) {
      token = await refreshAuthToken();
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    if (PAYMENT_DEBUG && isPaymentRequest(config.url)) {
      logPaymentRequest(config);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

flaskApi.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('authToken');
    if (token && !config.url?.startsWith('/auth/') && tokenExpiresSoon(token)) {
      token = await refreshAuthToken();
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const attachAuthResponseInterceptor = (client, { authoritative = true } = {}) => {
  client.interceptors.response.use(
    (response) => {
      if (PAYMENT_DEBUG && isPaymentRequest(response.config?.url)) {
        logPaymentResponse(response);
      }
      return response.data;
    },
    async (error) => {
      const originalRequest = error.config;
      if (PAYMENT_DEBUG && isPaymentRequest(originalRequest?.url)) {
        logPaymentError(error);
      }
      if (!originalRequest) {
        return Promise.reject(error.response?.data || error);
      }

      const errorText = [
        error.response?.data?.error,
        error.response?.data?.message,
        error.message,
      ].filter(Boolean).join(' ').toLowerCase();
      const isAuthEndpoint = originalRequest?.url?.includes('/auth/login')
        || originalRequest?.url?.includes('/auth/register')
        || originalRequest?.url?.includes('/auth/refresh');
      const isInvalidToken = errorText.includes('token is invalid')
        || errorText.includes('token has expired')
        || errorText.includes('unauthenticated');

      // Handle 401 Unauthorized
      if (error.response?.status === 401 && isInvalidToken && isAuthEndpoint) {
        clearStoredAuth();
        return Promise.reject(error.response?.data || error);
      }

      if (error.response?.status === 401 && isInvalidToken && originalRequest._retry) {
        // Only Laravel can invalidate the app session. A refreshed Laravel
        // token rejected by Flask indicates a service/configuration problem.
        if (!authoritative) {
          return Promise.reject({
            ...error.response.data,
            message: 'Le service DeepFace a refusé votre session. Vérifiez que son JWT_SECRET et sa base de données correspondent à ceux de Laravel.',
          });
        }
        clearStoredAuth();
        window.location.href = '/login';
        return Promise.reject(error.response?.data || error);
      }

      if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        originalRequest._retry = true;
        try {
          // Another request may have already refreshed this rejected token.
          let token = localStorage.getItem('authToken');
          if (!token || originalRequest.headers.Authorization === `Bearer ${token}`) {
            token = await refreshAuthToken();
          }
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return client(originalRequest);
        } catch (refreshError) {
          if (refreshError.response?.status === 401 || refreshError.response?.status === 403) {
            clearStoredAuth();
            window.location.href = '/login';
          }
          return Promise.reject(refreshError.response?.data || refreshError);
        }
      }

      return Promise.reject(error.response?.data || error);
    }
  );
};

// Response Interceptors with JWT Refresh
attachAuthResponseInterceptor(api);
attachAuthResponseInterceptor(flaskApi, { authoritative: false });

  export const authAPI = {
  register: (name, email, password, role, acceptedLegal = false) =>
    api.post('/auth/register', {
      name,
      email,
      password,
      role,
      accepted_terms: acceptedLegal,
      accepted_privacy: acceptedLegal,
    }),
  login: (email, password) =>
    api.post('/auth/login', { email, password }),
  logout: () =>
    api.post('/auth/logout'),
  getProfile: () =>
    api.get('/auth/me'),
  refresh: () =>
    api.post('/auth/refresh'),
  verifyTwoFactor: (userId, code) =>
    api.post('/auth/2fa/verify', { user_id: userId, code }),
  resendTwoFactor: (userId) =>
    api.post('/auth/2fa/resend', { user_id: userId }),
  verifyEmail: (email, token) =>
    api.post('/auth/verify-email', { email, token }),
  resendVerificationEmail: () =>
    api.post('/auth/resend-verification'),
};

// Auth
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);

// Account Management
export const getAccount = () => api.get('/account');
export const updateAccount = (data) => api.put('/account', data);
export const changePassword = (data) => api.put('/account/password', data);
export const enable2FA = () => api.post('/account/2fa/enable');
export const confirm2FA = (data) => api.post('/account/2fa/confirm', data);
export const disable2FA = (data) => api.post('/account/2fa/disable', data);

// Profile
export const uploadProfilePhoto = (formData) => {
  return api.post('/users/profile/photo', formData);
};

export const uploadProfileEmbedding = (formData, model = 'ArcFace') => {
  return flaskApi.post('/profile/embeddings', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    params: { model },
    timeout: 120000,
  });
};

export const getFlaskUploadUrl = (filename) => {
  if (!filename) return null;
  if (filename.startsWith('http://') || filename.startsWith('https://')) return filename;

  const base = FLASK_API_URL.replace(/\/api\/?$/, '');
  const clean = filename.replace(/^\/+/, '');
  return `${base}/uploads/${clean}`;
};

// Events
export const getEvents = () => api.get('/events');
export const getEvent = (eventId) => api.get(`/events/${eventId}`);

// Private event sharing: guests redeem the token from the QR/link, owners read
// back the link to display it.
export const redeemEventAccess = (eventId, token) =>
  api.post(`/events/${eventId}/access`, { token });
export const getEventShareLink = (eventId) => api.get(`/events/${eventId}/share-link`);

// Gallery lock: the owner closes the gallery while uploading; guests join the
// watchlist and are notified when it opens.
export const lockEventGallery = (eventId) => api.post(`/events/${eventId}/lock`);
export const unlockEventGallery = (eventId) => api.post(`/events/${eventId}/unlock`);
export const watchEventGallery = (eventId) => api.post(`/events/${eventId}/watch`);
export const unwatchEventGallery = (eventId) => api.delete(`/events/${eventId}/watch`);
export const createEvent = (data) => {
  if (data instanceof FormData) {
    return api.post('/events', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  return api.post('/events', data);
};
export const updateEvent = (eventId, data) => {
  if (data instanceof FormData) {
    data.append('_method', 'PUT');

    return api.post(`/events/${eventId}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  return api.put(`/events/${eventId}`, data);
};
export const deleteEvent = (eventId) => api.delete(`/events/${eventId}`);
export const uploadEventPhotos = (eventId, formData) => {
  return api.post(`/events/${eventId}/photos`, formData, {
    timeout: EVENT_PHOTO_UPLOAD_TIMEOUT_MS,
  });
};

// Event Photos Management
export const browseEventPhotos = (eventId) => api.get(`/events/${eventId}/photos/browse`);


export const downloadMultiplePhotos = (data) =>
  api.post('/photos/download-multiple', data, {
    responseType: 'blob',
    timeout: 120000,
  });

export const bulkSetPhotoPrice = (photoIds, price) =>
  api.put('/photos/bulk-price', { photo_ids: photoIds, price });

// Event manager: the roster of photographers whose work they may credit.
export const getManagerPhotographers = () => api.get('/manager/photographers');
export const addManagerPhotographer = (email) =>
  api.post('/manager/photographers', { email });
export const removeManagerPhotographer = (photographerId) =>
  api.delete(`/manager/photographers/${photographerId}`);

// Photo credit (a byline — it never moves the sale, which stays with the
// event's owner).
export const setPhotoPhotographer = (photoId, photographerId) =>
  api.put(`/photos/${photoId}/photographer`, { photographer_id: photographerId });
export const bulkSetPhotoPhotographer = (photoIds, photographerId) =>
  api.put('/photos/photographer/bulk', { photo_ids: photoIds, photographer_id: photographerId });

// A photographer's own byline across every event, including managed ones.
export const getCreditedPhotos = (eventId) =>
  api.get('/photographer/credited-photos', { params: eventId ? { event_id: eventId } : {} });

// Photos
export const getMyPhotos = () => api.get('/my-photos');
export const removeMyPhoto = (photoId) => api.delete(`/my-photos/${photoId}`);
export const downloadPhoto = (photoId) => {
  return api.get(`/photos/${photoId}/download`, {
    responseType: 'blob',
    timeout: 120000,
  });
};
export const fetchPhotoBlob = async (photoId) => {
  const blob = await api.get(`/photos/${photoId}/view`, {
    responseType: 'blob',
    timeout: 120000,
  });
  return URL.createObjectURL(blob);
};

// Subscription & Pricing
export const getSubscriptionPlans = () => api.get('/subscription/plans');
export const upgradeSubscription = (data) => {
  const planId = data?.new_plan_id || data?.plan || data;
  return api.post('/subscription/change', { new_plan_id: planId });
};

export const checkoutSubscriptionCinetPay = (planId, billingCycle, payment = {}) =>
  api.post('/subscription/checkout/cinetpay', { plan_id: planId, billing_cycle: billingCycle, payment });

export const checkoutSubscriptionPiSpi = (planId, billingCycle, payeurAlias) =>
  api.post('/subscription/checkout/pispi', { plan_id: planId, billing_cycle: billingCycle, payeur_alias: payeurAlias });

export const getSubscriptionCinetPayStatus = (merchantTransactionId) =>
  api.get(`/subscription/checkout/cinetpay/status/${encodeURIComponent(merchantTransactionId)}`);

export const getSubscriptionPiSpiStatus = (txId) =>
  api.get(`/subscription/checkout/pispi/status/${encodeURIComponent(txId)}`);

// Analytics
export const getAnalytics = () => api.get('/analytics');

// Get image URL
export const getImageUrl = (filename) => {

  //console.log("Image attempt:" + filename);
  if (!filename) return null;
  // Already a full URL
  if (filename.startsWith('http://') || filename.startsWith('https://')) return filename;
  const base = API_URL.replace('/api', '');
  // Strip any leading /storage/ the backend may have already included
  const clean = filename.replace(/^\/?(storage\/)?/, '');

  //console.log("Constructed image URL:", filename, file_url); // Debugging log
  return `${base}/storage/${clean}`;


};

/**
 * URL to display a photo with: the original for photos the viewer owns or that
 * are free, and the watermarked preview for everything else.
 *
 * The preview endpoint is public on purpose — <img> never sends the auth
 * header, so a token-gated URL would simply fail to load.
 */
export const getPhotoDisplayUrl = (photo) => {
  if (!photo) return null;

  const isFree = Number(photo.price || 0) <= 0;

  if (photo.has_purchased || photo.is_owner || isFree) {
    return getImageUrl(photo.filename);
  }

  return `${API_URL}/photos/${photo.id}/preview`;
};




// ... your existing code ...

// ============================================================================
// V2 ADDITIONS - Add these to your existing api.js
// ============================================================================

// Social Features (V2)
export const likePhoto = (photoId) => api.post(`/photos/${photoId}/like`);
export const unlikePhoto = (photoId) => api.delete(`/photos/${photoId}/like`);
export const sharePhoto = (photoId, platform) => 
  api.post(`/photos/${photoId}/share`, { platform });

// Slideshow (V2)
export const getGallerySlideshow = (galleryId) => 
  api.get(`/galleries/${galleryId}/slideshow`);

export const trackPhotoView = (photoId) => 
  api.post(`/photos/${photoId}/view`);

// Gallery Access (V2)
export const requestGalleryAccess = (galleryId, data) => 
  api.post(`/galleries/${galleryId}/request-access`, data);

export const getGalleryAccessRequests = (galleryId) => 
  api.get(`/galleries/${galleryId}/access-requests`);

export const approveAccessRequest = (requestId) => 
  api.post(`/access-requests/${requestId}/approve`);

export const denyAccessRequest = (requestId) => 
  api.post(`/access-requests/${requestId}/deny`);

export const getMyAccessRequests = () => 
  api.get('/my-access-requests');

// Photographer Profile (V2)
export const getPhotographerProfile = (photographerId) => 
  api.get(`/photographers/${photographerId}/profile`);

export const updatePhotographerProfile = (data) => 
  api.put('/photographer/profile', data);

export const generateQRCode = () => 
  api.post('/photographer/qr-code');

// Gallery Management (V2)
export const archiveGallery = (galleryId) => 
  api.post(`/galleries/${galleryId}/archive`);

export const restoreGallery = (galleryId) => 
  api.post(`/galleries/${galleryId}/restore`);

// Photo Management (V2)
export const excludePhotoFromMatching = (photoId) => 
  api.post(`/photos/${photoId}/exclude`);

export const bulkExcludePhotos = (photoIds) => 
  api.post('/photos/bulk-exclude', { photo_ids: photoIds });

// Payments (V2)
export const createCheckoutSession = (plan) => 
  api.post('/checkout', { plan });

export const getPaymentMethods = () => 
  api.get('/payment-methods');

export const addPaymentMethod = (data) => 
  api.post('/payment-methods', data);

export const deletePaymentMethod = (methodId) => 
  api.delete(`/payment-methods/${methodId}`);

// Notifications (V2)
export const getNotifications = () => 
  api.get('/notifications');

export const markNotificationRead = (notificationId) => 
  api.put(`/notifications/${notificationId}/read`);

export const markAllNotificationsRead = () => 
  api.post('/notifications/mark-all-read');

// Admin (V2)
export const getAdminUsers = () => 
  api.get('/admin/users');

export const updateAdminUser = (userId, data) => 
  api.put(`/admin/users/${userId}`, data);

export const suspendUser = (userId) => 
  api.post(`/admin/users/${userId}/suspend`);

export const getAdminAnalytics = () => 
  api.get('/admin/analytics');

export const getActivityLogs = () => 
  api.get('/admin/activity-logs');

// Contact & Marketing (V2)
export const submitContactForm = (data) => 
  api.post('/contact', data);

export const getDemoSlides = () => 
  api.get('/demo/slides');

export const getTestimonials = () => 
  api.get('/testimonials');




// Photo paiment api
export const purchasePhoto = (photoId) => 
  api.post(`/photos/${photoId}/purchase`);

export const checkoutPhotoCart = (photoIds, payment = {}) =>
  api.post('/purchases/checkout-cart', { photo_ids: photoIds, payment });

export const getCinetPayPaymentStatus = (merchantTransactionId) =>
  api.get(`/payments/cinetpay/status/${encodeURIComponent(merchantTransactionId)}`);

export const checkoutPhotoCartPiSpi = (photoIds, payeurAlias) =>
  api.post('/purchases/checkout-cart-pispi', { photo_ids: photoIds, payeur_alias: payeurAlias });

export const getPiSpiPaymentStatus = (txId) =>
  api.get(`/payments/pispi/status/${encodeURIComponent(txId)}`);

export const checkPhotoAccess = (photoId) => 
  api.get(`/photos/${photoId}/check-access`);

export const getPublicEvents = (params) => 
  api.get('/events/public', { params });

export const setPhotoPrice = (photoId, price) =>
  api.put(`/photos/${photoId}/price`, { price });

export const updatePhoto = (photoId, data) =>
  api.put(`/photos/${photoId}`, data);

export const deletePhoto = (photoId) =>
  api.delete(`/photos/${photoId}`);

export const getMyPurchases = () =>
  api.get('/purchases');

export const getPhotographerPaymentHistory = () =>
  api.get('/purchases/photographer-history');

export const downloadReceipt = (merchantTransactionId) => {
  return api.get(`/payments/${merchantTransactionId}/receipt`, {
    responseType: 'blob',
    timeout: 60000,
  });
};

// ---------------------------------------------------------------------------
// Face matching — two interchangeable providers:
//   deepface (Flask, boto-free, local models)
//   aws      (Laravel, AWS SDK for PHP — see photomatch-laravel/app/Services/AwsRekognitionService.php)
// ---------------------------------------------------------------------------
export const FACE_PROVIDERS = {
  DEEPFACE: 'deepface',
  REKOGNITION: 'aws',
};

// DeepFace: local models, no cloud cost, one model choice per scan.
export const scanEventForMyFace = (eventId, model = 'ArcFace') =>
  flaskApi.post(`/events/${eventId}/scan/deepface?model=${encodeURIComponent(model)}`, null, {
    timeout: FACE_SCAN_TIMEOUT_MS,
  });

export const scanEventDeepFace = (eventId, model = 'ArcFace') =>
  scanEventForMyFace(eventId, model);

// AWS Rekognition (Laravel, PHP SDK): the event's photos are indexed into a
// shared face collection on first use, then searched in a single call.
// `photoFile` lets the webcam flow search with a freshly captured face
// instead of the stored profile photo.
export const scanEventRekognition = (eventId, { photoFile = null, threshold, reindex = false } = {}) => {
  const params = new URLSearchParams();
  if (threshold != null) params.set('threshold', String(threshold));
  if (reindex) params.set('reindex', 'true');
  const query = params.toString() ? `?${params.toString()}` : '';

  if (!photoFile) {
    return api.post(`/events/${eventId}/scan/rekognition${query}`, null, {
      timeout: FACE_SCAN_TIMEOUT_MS,
    });
  }

  const formData = new FormData();
  formData.append('photo', photoFile);

  return api.post(`/events/${eventId}/scan/rekognition${query}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: FACE_SCAN_TIMEOUT_MS,
  });
};

// Provider-agnostic entry point.
export const scanEventForFaces = (eventId, provider, options = {}) => (
  provider === FACE_PROVIDERS.REKOGNITION
    ? scanEventRekognition(eventId, options)
    : scanEventForMyFace(eventId, options.model)
);

// Rekognition index management (status is readable by anyone who can see the
// event; indexing and teardown are photographer-only).
export const getRekognitionIndexStatus = (eventId) =>
  api.get(`/events/${eventId}/rekognition`);

export const indexEventRekognition = (eventId, { force = false } = {}) =>
  api.post(`/events/${eventId}/rekognition/index${force ? '?force=true' : ''}`, null, {
    timeout: FACE_SCAN_TIMEOUT_MS,
  });

export const deleteEventRekognitionIndex = (eventId) =>
  api.delete(`/events/${eventId}/rekognition`);

export const getPhotographerEarnings = () =>
  api.get('/photographer/earnings');


export default api;
